import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArchiveIcon, ArrowUpDownIcon, EllipsisVerticalIcon, PencilIcon, RotateCcwIcon, Trash2Icon } from 'lucide-react'
import {
	Avatar, AvatarFallback, AvatarImage, Button, ConfirmDialog, DropdownMenu, DropdownMenuContent,
	DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, IconButton,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import type { Property } from '@/types'
import { formatPropertyAddress } from '@/lib/property-address'
import { DEFAULT_LOCALE } from '@/types'
import { useGetMeQuery, useUpdatePropertyStatusMutation, useDeletePropertyMutation } from '@/api'
import { PropertyPublicationBadges } from './property-publication-badges'
import { PropertyStatusBadge } from './property-status-badge'
import { EditPropertySheet } from './edit-property-sheet'

// Handles both REST form ('ARCHIVED') and proto-string form ('PROPERTY_STATUS_ARCHIVED')
// because the gateway may return either depending on gRPC loader config.
const hasStatus = (property: Property, status: Property['status']) =>
	property.status === status || property.status.endsWith(`_${status}`)

function formatManagerName(name: string): string {
	const [surname, first, patronymic] = name.trim().split(/\s+/)
	if (!first) return surname
	return `${surname} ${first[0]}.${patronymic ? `${patronymic[0]}.` : ''}`
}

function propertyTitle(property: Property, t: (key: string) => string): string {
	const type = t(`properties.types.${property.type}`)
	return property.rooms ? `${type} ${property.rooms}-к` : type
}

function formatDateTime(iso: string): string {
	const ms = Date.parse(iso)
	if (Number.isNaN(ms)) return '—'
	return new Date(ms).toLocaleString(DEFAULT_LOCALE, {
		day: '2-digit', month: '2-digit', year: 'numeric',
		hour: '2-digit', minute: '2-digit',
	})
}

interface PropertiesTableProps {
	properties: Property[]
	/**
	 * `drafts` swaps the status / publication / activate cluster for the last
	 * edited timestamp. A draft is always DRAFT, is never published, and is
	 * activated from its own page after the remaining fields are filled in —
	 * so those three columns carry no information on that list.
	 *
	 * `archive` drops the status column for the same reason — every row reads
	 * "Archived" — and promotes unarchiving from the row menu to a button,
	 * since it is the one thing anyone comes to this list to do.
	 *
	 * One prop rather than a pile of booleans: what each list shows is a
	 * single decision, not a set of independent toggles.
	 */
	variant?: 'default' | 'drafts' | 'archive'
	/**
	 * Drafts open in the wizard to be finished rather than on the read-only
	 * card (#285). When set, a row click calls this instead of navigating; the
	 * card stays reachable through the row menu, since it carries Документы
	 * and Ответственный which the wizard has no equivalent for.
	 */
	onContinueDraft?: (property: Property) => void
}

type SortField = 'address' | 'manager' | 'updated'
type SortDir   = 'asc' | 'desc'

export function PropertiesTable({ properties, variant = 'default', onContinueDraft }: PropertiesTableProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { data: me } = useGetMeQuery()
	const isDrafts  = variant === 'drafts'
	const isArchive = variant === 'archive'
	const [editProperty, setEditProperty] = useState<Property | null>(null)
	const [deleteTarget, setDeleteTarget] = useState<Property | null>(null)
	// Drafts open on most-recently-edited first — the point of the column is
	// picking up where you left off.
	const [sortField, setSortField] = useState<SortField>(isDrafts ? 'updated' : 'address')
	const [sortDir,   setSortDir]   = useState<SortDir>(isDrafts ? 'desc' : 'asc')
	const [updateStatus] = useUpdatePropertyStatusMutation()
	const [deleteProperty] = useDeletePropertyMutation()
	const setStatus = (id: string, status: Property['status']) =>
		void updateStatus({ id, body: { status } })

	function toggleSort(field: SortField) {
		if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		else { setSortField(field); setSortDir('asc') }
	}

	const sorted = useMemo(() => [...properties].sort((a, b) => {
		const dir = sortDir === 'asc' ? 1 : -1
		if (sortField === 'manager') {
			return (a.manager?.name ?? '').localeCompare(b.manager?.name ?? '') * dir
		}
		if (sortField === 'updated') {
			// `|| 0` also absorbs NaN from a missing or malformed timestamp, so
			// those rows sort as epoch rather than scrambling the comparator.
			return ((Date.parse(a.updatedAt) || 0) - (Date.parse(b.updatedAt) || 0)) * dir
		}
		// Sort on the string actually rendered: composing it by hand here would
		// order rows by a label nobody sees, so a suggester-filled address (which
		// repeats the city) would jump out of alphabetical position.
		return formatPropertyAddress(a.city, a.address)
			.localeCompare(formatPropertyAddress(b.city, b.address)) * dir
	}), [properties, sortField, sortDir])

	return (
		<>
		{editProperty && (
			<EditPropertySheet
				property={editProperty}
				open
				onOpenChange={(open) => { if (!open) setEditProperty(null) }}
			/>
		)}
		<ConfirmDialog
			open={!!deleteTarget}
			onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
			title={t('properties.deleteDialog.title')}
			description={t('properties.deleteDialog.description')}
			confirmLabel={t('properties.delete')}
			cancelLabel={t('common.cancel')}
			variant="destructive"
			onConfirm={() => { void deleteProperty(deleteTarget!.id); setDeleteTarget(null) }}
		/>
		<div className="flex-1 overflow-auto px-4">
			<div className="rounded-lg border border-(--color-border) overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead className="text-(--color-muted-foreground)">
								<Button
									variant="ghost"
									iconRight={<ArrowUpDownIcon className={`size-3.5${sortField === 'address' ? ' text-(--color-foreground)' : ''}`} />}
									className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
									onClick={() => toggleSort('address')}
								>
									{t('properties.table.property')}
								</Button>
							</TableHead>
							<TableHead className="text-(--color-muted-foreground)">
								<Button
									variant="ghost"
									iconRight={<ArrowUpDownIcon className={`size-3.5${sortField === 'manager' ? ' text-(--color-foreground)' : ''}`} />}
									className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
									onClick={() => toggleSort('manager')}
								>
									{t('properties.table.manager')}
								</Button>
							</TableHead>
							{isDrafts && (
								<TableHead className="text-(--color-muted-foreground)">
									<Button
										variant="ghost"
										iconRight={<ArrowUpDownIcon className={`size-3.5${sortField === 'updated' ? ' text-(--color-foreground)' : ''}`} />}
										className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
										onClick={() => toggleSort('updated')}
									>
										{t('properties.table.updatedAt')}
									</Button>
								</TableHead>
							)}
							{!isDrafts && !isArchive && (
								<TableHead className="w-46 text-(--color-muted-foreground) font-[500]">{t('properties.table.status')}</TableHead>
							)}
							{!isDrafts && (
								<TableHead className="text-(--color-muted-foreground) font-[500]">{t('properties.table.publication')}</TableHead>
							)}
							<TableHead className="w-16" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{sorted.map((property) => (
							<TableRow
								key={property.id}
								className="h-24 cursor-pointer"
								onClick={(e) => {
									if ((e.target as Element).closest('button,[role="button"]')) return
									if (onContinueDraft) { onContinueDraft(property); return }
									void navigate({ to: '/properties/$propertyId', params: { propertyId: property.id } })
								}}
							>
								<TableCell className="px-4">
									<div className="flex items-center gap-3">
										{/* Landscape and larger than the square it replaced.
										    Down a long list the photo is what a row is
										    recognised by — the title is «Квартира» on half of
										    them — and a 64px square cropped away most of what
										    made one flat tell apart from another. */}
										{property.images?.[0] ? (
											<img
												src={property.images[0].url}
												alt={propertyTitle(property, t)}
												className="h-20 w-28 shrink-0 rounded-xl object-cover"
											/>
										) : (
											<div className="h-20 w-28 shrink-0 rounded-xl bg-(--color-muted)" />
										)}
										<div className="flex flex-col gap-0.5 min-w-0">
											<span className="font-[500] text-(--color-foreground) leading-none truncate">
												{propertyTitle(property, t)}
											</span>
											<span className="text-sm text-(--color-muted-foreground) leading-5 truncate">
												{formatPropertyAddress(property.city, property.address)}
											</span>
										</div>
									</div>
								</TableCell>
								<TableCell className="px-4">
									{property.manager ? (
										<div className="flex items-center gap-2">
											<Avatar className="size-6 shrink-0">
												<AvatarImage src={property.manager.avatarUrl} alt={property.manager.name} />
												<AvatarFallback className="text-xs">
													{property.manager.name.charAt(0).toUpperCase()}
												</AvatarFallback>
											</Avatar>
											<span className="text-sm text-(--color-foreground)">
												{property.manager.id === me?.id
													? t('properties.me')
													: formatManagerName(property.manager.name)}
											</span>
										</div>
									) : (
										<span className="text-sm text-(--color-muted-foreground)">{t('properties.unassigned')}</span>
									)}
								</TableCell>
								{isDrafts && (
									<TableCell className="px-4 text-sm text-(--color-muted-foreground) whitespace-nowrap">
										{formatDateTime(property.updatedAt)}
									</TableCell>
								)}
								{!isDrafts && !isArchive && (
									<TableCell className="w-46 px-4">
										<PropertyStatusBadge status={property.status} />
									</TableCell>
								)}
								{!isDrafts && (
									<TableCell className="px-4">
										<PropertyPublicationBadges publications={property.publications ?? []} />
									</TableCell>
								)}
								<TableCell className="px-4 text-right" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
									{!isDrafts && hasStatus(property, 'DRAFT') && (
										<Button
											size="sm"
											variant="default"
											className="mr-2"
											onClick={() => setStatus(property.id, 'AVAILABLE')}
										>
											{t('properties.activate')}
										</Button>
									)}
									{isArchive && hasStatus(property, 'ARCHIVED') && (
										<Button
											size="sm"
											variant="default"
											className="mr-2"
											onClick={() => setStatus(property.id, 'AVAILABLE')}
										>
											{t('properties.unarchive')}
										</Button>
									)}
									<DropdownMenu>
										<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('common.actions')} />}>
											<EllipsisVerticalIcon className="size-4" />
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end" className="w-auto min-w-40">
											{/* Черновику остаётся только удаление. Редактируют его в
											    самом мастере — по строке, — карточки у него нет,
											    пока не заполнены обязательные поля, а архивировать
											    незавершённую заготовку нечего: она и так не в работе. */}
											{!isDrafts && (
												<>
													<DropdownMenuItem onClick={() => setEditProperty(property)}>
														<PencilIcon />
														{t('properties.edit')}
													</DropdownMenuItem>
													{/* On the archive list unarchiving is already a button, so the
													    menu would only repeat it. */}
													{hasStatus(property, 'ARCHIVED') ? !isArchive && (
														<DropdownMenuItem onClick={() => setStatus(property.id, 'AVAILABLE')}>
															<RotateCcwIcon />
															{t('properties.unarchive')}
														</DropdownMenuItem>
													) : (
														<DropdownMenuItem onClick={() => setStatus(property.id, 'ARCHIVED')}>
															<ArchiveIcon />
															{t('properties.archive')}
														</DropdownMenuItem>
													)}
													<DropdownMenuSeparator />
												</>
											)}
											<DropdownMenuItem variant="destructive" onClick={() => setDeleteTarget(property)}>
												<Trash2Icon />
												{t('properties.delete')}
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
		</>
	)
}
